// CLI v0.8 Module 3 — `cloudgrid plug` verb (Tasks M3.4–M3.7 runtime).
//
// Spec: docs/strategy/cli-ux-spec-v17.md §7.
// Decision: docs/decisions/012-plug-verb-and-charged-state.md.
//
// The plug verb is the centerpiece of the CLI. It puts a node on the grid:
//
//   cloudgrid plug                   # in a linked dir → re-plug current source
//   cloudgrid plug --existing <id>   # bind this dir to an existing entity, then plug
//   cloudgrid plug --no-deploy       # init-only semantics; entity stays 'charged'
//   cloudgrid plug ./deck.pptx       # inspiration upload (DEFERRED → friendly error)
//
// This file implements the runtime paths only. Inspiration upload and
// auto-init-from-folder are concerns of later subagents.
//
// `runPlug` is the test seam — verb tests construct a `MockV2Client`,
// build a temp project tree, and call `runPlug` directly. The commander
// `plugCommand` is a thin wrapper that resolves the real V2Client.

import { Command } from 'commander';
import { existsSync, readFileSync } from 'fs';
import { dirname, join } from 'path';
import { fileURLToPath } from 'url';
import { parse as parseYaml } from 'yaml';
import inquirer from 'inquirer';

import { CloudGridError } from '../utils.js';
import {
  getV2Client,
  type IV2Client,
  type PlugResult,
  type PlugWarning,
} from '../api/v2-client.js';
import { V2ApiError } from '../api/v2-errors.js';
import {
  getActiveOrg,
  getActiveOrgWithList,
  notSignedInMessage,
} from '../active-org.js';
import {
  appendGitignoreLine,
  readLink,
  readLinkOrRecover,
  validateLinkVsActiveOrg,
  writeLink,
  type LinkArtifact,
} from '../services/link.js';
import { buildServiceTarball, resolveServiceSourceDir } from '../services/tarball.js';
import { pollDeploy, type DeployTrace } from '../services/plug-poll.js';
import {
  pollPlugTask,
  PlugTaskFailedError,
  type PlugTaskResult,
} from '../services/task-poll.js';
import { MultiStepRenderer } from '../render/multi-step.js';
import { renderTerminalState } from '../render/terminal-state.js';
import { renderPlugVisibilityBlock } from '../services/visibility-format.js';
import {
  hasStructuredDeployError,
  renderStructuredDeployError,
} from '../render/deploy-error.js';
import { handleV2Error } from '../handle-error.js';
import {
  buildLogTailFor as defaultBuildLogTailFor,
  resolveBuildId,
} from '../services/build-log-tail.js';
import { runtimeErrorTailFor as defaultRuntimeErrorTailFor } from '../services/runtime-error-tail.js';
import { BuildProgressTracker } from '../services/build-progress.js';
import { osNotify as defaultOsNotify } from '../services/os-notify.js';
import {
  appendDeployLog as defaultAppendDeployLog,
  formatElapsedHuman,
  type DeployLogEntry,
} from '../services/deploys-log.js';
import { configExists, loadConfig } from '../config.js';
import { collectDeclaredEnvKeys } from '../services/env-preflight.js';
import { runEnvPreflight } from '../services/env-preflight-flow.js';
import {
  detectTsServices,
  runTsPreflight,
  defaultTsCheckRunner,
  type TsServiceTarget,
  type TsCheckRunner,
} from '../services/ts-preflight.js';
import { runInspirationPlug } from './plug-inspiration.js';
import {
  runAutoInit,
  type AutoInitDeps,
} from '../services/plug-auto-init.js';
import {
  spawnDetachedWatcher as defaultSpawnDetachedWatcher,
  type SpawnResult,
} from '../services/detach-spawn.js';
import { addGridOption, gridSlug, type GridOpts } from '../grid-opt.js';

// ─────────────────────────────────────────────────────────────────────────────
// CLI version (mirrors commands/init.ts)
// ─────────────────────────────────────────────────────────────────────────────

const __dirname = dirname(fileURLToPath(import.meta.url));
function findPkgVersion(startDir: string): string {
  let dir = startDir;
  for (let i = 0; i < 6; i++) {
    const candidate = join(dir, 'package.json');
    try {
      const pkg = JSON.parse(readFileSync(candidate, 'utf-8'));
      if (
        pkg.name === '@cloudgrid-io/cli' &&
        typeof pkg.version === 'string'
      ) {
        return pkg.version;
      }
    } catch {
      // fall through to parent
    }
    const parent = dirname(dir);
    if (parent === dir) break;
    dir = parent;
  }
  return '0.0.0';
}
const PKG_VERSION: string = findPkgVersion(__dirname);

// ─────────────────────────────────────────────────────────────────────────────
// Public types
// ─────────────────────────────────────────────────────────────────────────────

export interface PlugOpts extends GridOpts {
  existing?: string;
  noDeploy?: boolean;
  // Inspiration-only flags (declared so commander lists them in --help).
  scope?: string;
  space?: string;
  visibility?: string;
  name?: string;
  expires?: string;
  pinReason?: string;
  fromClaude?: boolean;
  // Pass-throughs.
  inspiredBy?: string[];
  displayName?: string;
  noClipboard?: boolean;
  json?: boolean;
  /**
   * Backlog 072-C — opt out of the OS-level notification on terminal state.
   * Commander wires `--no-notify` to `notify: false`, so the runner accepts
   * either spelling. The `noNotify` form is the canonical one used by
   * subagent-driven and integration tests.
   */
  notify?: boolean;
  noNotify?: boolean;
  /**
   * Backlog 073-B — proceed even when `services/<svc>/.env.example` declares
   * keys that are unset on the entity. Without this flag, missing keys
   * abort plug before upload (TTY: prompt; non-TTY: error).
   */
  forceMissingEnv?: boolean;
  /**
   * Backlog 071-A — opt out of the live Cloud Build progress lines under
   * the cloud_build spinner. Default on (TTY only). Commander wires
   * `--no-progress` to `progress: false`; both spellings are accepted.
   */
  progress?: boolean;
  noProgress?: boolean;
  /**
   * Backlog 544 — override the poll budget, in minutes. Defaults to
   * `DEFAULT_MAX_DURATION_MS` (20m) when unset. For a genuinely long cold
   * build, `--timeout 40` keeps the CLI attached past the default ceiling.
   */
  timeout?: string;
  /**
   * Backlog 072-A — queue the deploy and exit immediately. A detached
   * child process polls the trace to terminal state and fires the OS
   * notification + deploys.log entry on the user's behalf.
   */
  detach?: boolean;
  /**
   * Backlog 091 Layer 2 — opt-in legacy-namespace migration. Set when
   * the operator passes `--migrate-from-legacy <ns>` (alias `--from-legacy`).
   * The CLI validates the basic shape (DNS-1123 label) and forwards the
   * value to the API as the multipart field `migrate_from_legacy_namespace`.
   * Server-side, the deployer inserts a one-time cutover step that releases
   * the legacy namespace's hostname claim, runs apply_manifests + rollout
   * normally, then drains + deletes the legacy compute. See backlog 091
   * and `docs/operations/2026-05-07-legacy-pre-cutover-migration.md`.
   */
  migrateFromLegacy?: string;
  /**
   * Bypass the client-side preflights. Set when the operator passes
   * `--skip-preflight`. Covers both the backlog 091 Layer 3 legacy_pre_cutover
   * warning prompt and the backlog 015-B TypeScript preflight (`tsc --noEmit`).
   * Useful for CI flows that already gate type-checking elsewhere, or to deploy
   * past a known type error.
   */
  skipPreflight?: boolean;
  /**
   * Backlog 187 — print the underlying technical detail alongside the
   * user-language error message on deploy failure. By default the
   * structured `message_user` is the only thing the user sees; the
   * `message_technical` (raw K8s/build output) is hidden so the user gets
   * an actionable surface, not a stack trace. `--verbose` un-hides it.
   */
  verbose?: boolean;
  /**
   * Option E (brainstorm 2026-06-04) — when `plug` runs in a directory with no
   * `.cloudgrid/link.json` and no `cloudgrid.yaml`, auto-detect the framework,
   * register an entity, generate the yaml, and plug — all in one command.
   * `--auto` skips the confirm prompt (CI). `--no-auto-init` opts out entirely
   * (commander maps it to `autoInit: false`) and restores the old hard error.
   */
  auto?: boolean;
  autoInit?: boolean;
  /**
   * ADR 036 V3 (spec §4.6) — re-mint a new version from an OLDER version's
   * source. Downloads that version's source + cloudgrid.yaml into a temp
   * checkout linked to the same entity, then plugs it normally so the server
   * rebuilds the old bytes and mints a fresh version. Useful for "undo my last
   * experiment, redeploy what worked." Requires a linked directory.
   */
  from?: string;
}

export interface PlugDeps {
  client?: IV2Client;
  cwd?: string;
  prompt?: (
    q: string,
    opts?: { default?: string },
  ) => Promise<string>;
  /** Test injection for the multi-step renderer's writer. */
  writer?: (s: string) => void;
  /** Test injection — overrides the polling sleep adapter. */
  sleep?: (ms: number) => Promise<void>;
  /** Test injection — passed through to pollDeploy. */
  pollIntervalMs?: number;
  /** Test injection — passed through to pollDeploy. */
  maxDurationMs?: number;
  /** Test injection — when set, MultiStepRenderer is forced into CI mode. */
  tty?: boolean;
  /**
   * Backlog 071-A — fetcher for the Cloud Build log tail. Returns a
   * ready-to-print string (without trailing newline) or null when there's
   * nothing useful to surface. Tests pass `() => Promise.resolve(null)` to
   * assert no fallout from the build-log code path.
   */
  buildLogTail?: typeof defaultBuildLogTailFor;
  /**
   * Backlog 071-B — renderer for typed runtime errors (CrashLoopBackOff,
   * ImagePullBackOff, OOMKilled) emitted by the API's wait_rollout step.
   * Returns a ready-to-print string (no trailing newline) or null when the
   * failure isn't a typed runtime error. Tests inject a stub when they need
   * to assert the exact rendered shape.
   */
  runtimeErrorTail?: typeof defaultRuntimeErrorTailFor;
  /**
   * Backlog 072-C — desktop notifier. Tests pass `() => Promise.resolve(false)`
   * so no rogue OS notifications fire during CI.
   */
  notify?: typeof defaultOsNotify;
  /**
   * Backlog 072-D — append a line to ~/.cloudgrid/deploys.log. Tests
   * either provide a no-op or a temp-home-bound version to assert the
   * line shape without touching the user's real log.
   */
  appendDeployLog?: typeof defaultAppendDeployLog;
  /**
   * Test seam for the wall-clock used to compute elapsed time. Defaults to
   * `Date.now`. Tests pass a stubbed clock so the formatted "live in 4m 32s"
   * line is deterministic.
   */
  now?: () => number;
  /**
   * Backlog 073 — preflight prompt injectors. Tests pass array-driven stubs
   * so we never block on real stdin. Defaults wire inquirer's `confirm` and
   * `password` types.
   */
  preflightConfirm?: (
    message: string,
    defaultValue?: boolean,
  ) => Promise<boolean>;
  preflightInput?: (
    message: string,
    opts?: { hidden?: boolean },
  ) => Promise<string>;
  /**
   * Backlog 015-B — injectable `tsc --noEmit` runner for the client-side
   * TypeScript preflight. Defaults to a runner that resolves tsc via
   * `npx --no-install` (honoring the user's local TypeScript install). Tests
   * pass a stub so no real tsc shells out in CI.
   */
  tsCheck?: TsCheckRunner;
  /**
   * Test seam for the clipboard write fired after a successful inspiration
   * upload. Tests pass a vi.fn() to assert that it is or isn't called based
   * on `--no-clipboard`. The default helper shells out to pbcopy / xclip /
   * clip.exe and never throws.
   */
  copyToClipboard?: (text: string) => Promise<boolean>;
  /**
   * Backlog 071-A — factory for the Cloud Build progress tracker. Defaults
   * to a real tracker that shells out to gcloud. Tests pass a stub so no
   * real `gcloud` runs in CI.
   */
  buildProgressTracker?: () => BuildProgressTracker;
  /**
   * Backlog 072-A — test seam for the detached-watcher spawn. Tests pass
   * a stub that records the invocation without forking a real process.
   */
  spawnWatcher?: typeof defaultSpawnDetachedWatcher;
  /**
   * Test seam for `validateConfig`'s warn channel. Defaults to
   * `process.stderr.write` so the user sees yaml-shape advisories
   * (deprecated-field migrations like `network.public` → `expose`, the
   * path-absent soft warning) before the build kicks off. Backlog 212
   * phase 2 (0.8.15) moved unknown-key handling from this channel to
   * die(); the channel now carries only the remaining soft warnings.
   * Tests pass a no-op or an array-collecting stub so the warning text
   * is asserted-on without polluting the test process's real stderr.
   */
  validateWarn?: (message: string) => void;
  /**
   * Option E — test injection for the `plug` auto-init flow. Tests pass a
   * stub framework detector, confirm, slug-prompt, and TTY value so the
   * unlinked-dir path is exercised hermetically (no real stdin / fs sniff
   * surprises). The `cliVersion` is supplied by `runPlug`; tests only need
   * to override the behavioural fields.
   */
  autoInitDeps?: Partial<Omit<AutoInitDeps, 'cliVersion'>>;
  /**
   * ADR 036 V3 — test seam for `plug --from`. The orchestration in
   * `runPlugFromVersion` delegates the actual plug to this function (defaults
   * to `runPlug`) so a test can assert the staged cwd + stripped `from` without
   * driving the whole pipeline.
   */
  plugFn?: (target: string | undefined, opts: PlugOpts, deps: PlugDeps) => Promise<void>;
}

// ─────────────────────────────────────────────────────────────────────────────
// Cloudgrid YAML shape (subset relevant to plug).
// ─────────────────────────────────────────────────────────────────────────────

interface CloudgridYaml {
  name?: string;
  services?: Record<string, unknown>;
  agent?: Record<string, unknown>;
  requires?: unknown[];
  [k: string]: unknown;
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

function loadCloudgridYaml(projectRoot: string): {
  text: string;
  parsed: CloudgridYaml;
} {
  const path = join(projectRoot, 'cloudgrid.yaml');
  if (!existsSync(path)) {
    throw new CloudGridError(`cloudgrid.yaml not found in ${projectRoot}`);
  }
  const text = readFileSync(path, 'utf-8');
  let parsed: unknown;
  try {
    parsed = parseYaml(text);
  } catch (err) {
    throw new CloudGridError(
      `cloudgrid.yaml is not valid YAML: ${(err as Error).message}`,
    );
  }
  if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
    throw new CloudGridError(
      'cloudgrid.yaml must be a YAML mapping at the top level',
    );
  }
  return { text, parsed: parsed as CloudgridYaml };
}

/**
 * Map a V2ApiError from the plug or runtime-fetch endpoints into a friendly
 * CloudGridError per spec v17 §7.5.
 */
function mapPlugServerError(
  err: V2ApiError,
  ctx: { entityIdOrSlug: string },
): CloudGridError {
  if (
    err.code === 'AUTH_REQUIRED' ||
    err.code === 'AUTH_EXPIRED' ||
    err.code === 'AUTH_INVALID'
  ) {
    return new CloudGridError(notSignedInMessage());
  }
  if (err.code === 'FORBIDDEN_ROLE') {
    return new CloudGridError(
      'Plugging an entity requires admin or developer role on this grid.',
    );
  }
  if (err.code === 'NOT_FOUND' || err.code.endsWith('_NOT_FOUND')) {
    return new CloudGridError(
      [
        `Entity ${ctx.entityIdOrSlug} not found.`,
        'Verify the id or run `cloudgrid grid` to list yours.',
      ].join('\n'),
    );
  }
  if (
    err.code === 'ALREADY_ARCHIVED' ||
    err.code === 'ENTITY_ARCHIVED' ||
    err.code === 'ENTITY_EXPIRED'
  ) {
    return new CloudGridError(
      `${ctx.entityIdOrSlug} is archived. Use \`cloudgrid plug --restore\` (coming soon) or recreate.`,
    );
  }
  if (err.code === 'VALIDATION_FAILED') {
    return new CloudGridError(err.message);
  }
  return new CloudGridError(
    `Server error (${err.status} ${err.code}). Try again, or run cloudgrid feedback if it persists.`,
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Inner runner
// ─────────────────────────────────────────────────────────────────────────────

/**
 * ADR 036 V3 — `plug --from <version_id>`. Stage a temp checkout of the named
 * version (its source + cloudgrid.yaml), linked to the same entity, then run
 * the normal plug pipeline against it. The server rebuilds the old bytes and
 * mints a fresh version, advancing HEAD — exactly "undo my experiment, redeploy
 * what worked." Requires a linked directory.
 *
 * `deps.plugFn` is injectable so the orchestration is unit-testable without
 * driving the full plug pipeline.
 */
export async function runPlugFromVersion(opts: PlugOpts, deps: PlugDeps = {}): Promise<void> {
  const { mkdtempSync, mkdirSync, writeFileSync } = await import('fs');
  const { tmpdir } = await import('os');
  const { downloadEntitySource } = await import('./pull.js');

  const cwd = deps.cwd ?? process.cwd();
  const client = deps.client ?? (await getV2Client());

  const link = readLink(cwd);
  if (!link) {
    throw new CloudGridError(
      'plug --from must run in a directory linked to an entity. Run `cloudgrid clone <id>` first.',
    );
  }
  const versionId = opts.from!.trim();
  if (versionId.length === 0) {
    throw new CloudGridError('plug --from needs a version id, e.g. cloudgrid plug --from v_a1b2c3d');
  }

  // Stage the version's source + yaml into a throwaway checkout linked to the
  // same entity. `force: true` because the temp dir is ours to own.
  const stageDir = mkdtempSync(join(tmpdir(), 'cg-plug-from-'));
  await downloadEntitySource({
    client,
    entityId: link.entity_id,
    targetDir: stageDir,
    force: true,
    version: versionId,
    quiet: !!opts.json,
  });
  const yamlResp = await client.get<{ yaml: string }>(
    `/api/v2/runtimes/${encodeURIComponent(link.entity_id)}/cloudgrid-yaml`,
    { version: versionId },
  );
  writeFileSync(join(stageDir, 'cloudgrid.yaml'), yamlResp.yaml, 'utf-8');
  mkdirSync(join(stageDir, '.cloudgrid'), { recursive: true });
  writeLink(link, stageDir);

  // Re-enter the normal pipeline from the staged dir. Strip `from` so we don't
  // recurse, and force the cwd to the staging directory.
  const { from: _from, ...plugOpts } = opts;
  const plugFn = deps.plugFn ?? runPlug;
  await plugFn(undefined, plugOpts, { ...deps, client, cwd: stageDir });
}

export async function runPlug(
  target: string | undefined,
  opts: PlugOpts,
  deps: PlugDeps = {},
): Promise<void> {
  const cwd = deps.cwd ?? process.cwd();

  // ADR 036 V3 — `plug --from <version_id>` re-mints from an older version's
  // source. Handled before the inspiration / linked-dir branches: it stages a
  // temp checkout of that version and recurses into the normal plug pipeline.
  if (opts.from !== undefined && opts.from !== '') {
    return runPlugFromVersion(opts, deps);
  }

  // Inspiration upload path — fires when a positional target was passed.
  // Spec v17 §7.2; contract `docs/contracts/inspiration-api.md` §4.
  if (target !== undefined && target !== '') {
    const client = deps.client ?? (await getV2Client());
    return runInspirationPlug(client, target, opts, deps);
  }

  // Inspiration-only flags without a target are a user error: the flags
  // mean nothing without a payload. Surface the same hint the rest of the
  // CLI would on a misconfigured call.
  if (
    opts.scope ||
    opts.space ||
    opts.visibility ||
    opts.name ||
    opts.expires ||
    opts.pinReason ||
    opts.fromClaude
  ) {
    throw new CloudGridError(
      'Inspiration flags require a target path. Run: cloudgrid plug ./<file>',
    );
  }

  // ── --existing: bind a fresh checkout to a server entity, then plug. ──
  if (opts.existing !== undefined) {
    const existingId = opts.existing.trim();
    if (existingId.length === 0 || existingId.length > 64) {
      throw new CloudGridError(
        `--existing entity-id is not in a recognised format: '${opts.existing}'`,
      );
    }

    if (readLink(cwd) !== null) {
      throw new CloudGridError(
        [
          'Already linked. Run plug without --existing.',
          'To re-link, remove .cloudgrid/ first.',
        ].join('\n'),
      );
    }

    const grid = gridSlug(opts);
    const baseClient = deps.client ?? (await getV2Client(grid));

    // Server-authoritative active-grid resolution. Throws CloudGridError
    // with the right wording on every failure mode. `--grid <slug>` (backlog
    // 308) takes precedence over the persisted active grid for this call.
    const activeOrg = await getActiveOrg(baseClient, { override: grid });

    // Backlog 320 — bind the client to the *resolved* org for every request
    // from here on (entity lookup + the deploy submission). Without this the
    // client keeps sending the persisted active-org slug as `x-cloudgrid-org`,
    // so `--org system` resolves system for the checks above but the server
    // still receives the active org's slug on the actual plug — the entity
    // lookup then happens in the wrong org and 404s.
    const client = baseClient.withOrgSlug(activeOrg.slug);

    // Verify the entity exists; the response gives us the canonical org
    // info to write into link.json. The mock has a default for this route
    // that returns a runtime-shaped object, so this works in tests too.
    let runtime:
      | {
          entity_id?: string;
          name?: string;
          kind?: 'app' | 'agent';
          org?: { org_id?: string; slug?: string };
        }
      | undefined;
    try {
      runtime = await client.get<typeof runtime>(
        `/api/v2/runtimes/${existingId}`,
      );
    } catch (err) {
      if (err instanceof V2ApiError) {
        throw mapPlugServerError(err, { entityIdOrSlug: existingId });
      }
      throw err;
    }

    const orgIdFromRuntime = runtime?.org?.org_id ?? activeOrg.org_id;
    const orgSlugFromRuntime = runtime?.org?.slug ?? activeOrg.slug;

    // Backlog 066: refuse to silently classify as 'app'. Decision 012 §4
    // names `kind` as required on link.json; the runtime endpoint should
    // carry the same field. A missing or unrecognised `kind` is a server
    // bug, not an input we should paper over with a default.
    const runtimeKind = runtime?.kind;
    if (runtimeKind !== 'app' && runtimeKind !== 'agent') {
      throw new CloudGridError(
        `Server returned entity ${existingId} without a valid 'kind' field. ` +
          `Expected 'app' or 'agent'. Report this as a bug.`,
      );
    }

    const link: LinkArtifact = {
      entity_id: existingId,
      org_id: orgIdFromRuntime,
      org_slug: orgSlugFromRuntime,
      kind: runtimeKind,
      // Per decision 015 §"Identity-aware behavior" — write the
      // server-canonical slug so the dev runner doesn't fall back to yaml
      // `name:` or `basename(cwd)`. `runtime?.name` is the disambiguated
      // slug returned by GET /api/v2/runtimes/:id.
      ...(runtime?.name ? { entity_slug: runtime.name } : {}),
      created_at: new Date().toISOString(),
      cli_version: PKG_VERSION,
    };
    writeLink(link, cwd);
    appendGitignoreLine('.cloudgrid/', cwd);

    if (!opts.json) {
      const displayName = runtime?.name ?? existingId;
      process.stdout.write(
        `Linking this directory to ${displayName} (${orgSlugFromRuntime})...\n` +
          '  .cloudgrid/link.json written.\n',
      );
    }

    if (opts.noDeploy) {
      if (opts.json) {
        process.stdout.write(
          JSON.stringify({ status: 'linked', entity_id: existingId }) + '\n',
        );
      } else {
        process.stdout.write('  Linked. Charged. No deploy queued.\n');
      }
      return;
    }

    return runLinkedDirPlug(client, link, cwd, opts, deps);
  }

  // Linked-dir plug — the §7.4 default path when no positional argument.
  // Backlog 090: readLinkOrRecover heals a kind-less link.json (every entity
  // plugged with CLI ≤ 0.8.6 is in this state) by querying the v2 entity
  // API and writing the recovered link back to disk. Falls back to 'app'
  // with a stderr warning if the API can't help.
  // `--grid <slug>` (primary) / `--org <slug>` (deprecated alias) — resolve the
  // effective context grid once for this linked-dir path.
  const grid = gridSlug(opts);
  const baseClient = deps.client ?? (await getV2Client(grid));
  let link = await readLinkOrRecover(baseClient, cwd);
  if (link === null) {
    // Option E (brainstorm 2026-06-04) — auto-init when the directory has no
    // link AND no cloudgrid.yaml. A yaml-present-but-unlinked directory is
    // ambiguous (it may belong to a teammate's existing server entity), so we
    // leave that to the explicit `--existing` / `clone` paths and keep the old
    // guidance. `--no-auto-init` opts out of auto-init entirely.
    const hasYaml = existsSync(join(cwd, 'cloudgrid.yaml'));
    if (opts.autoInit === false || hasYaml) {
      throw new CloudGridError(
        [
          "This directory isn't linked to a CloudGrid node.",
          'Run: cloudgrid init <kind> <slug>   to start a new node, or',
          '     cloudgrid plug --existing <entity-id>   to bind to an existing one.',
        ].join('\n'),
      );
    }

    const result = await runAutoInit(
      baseClient,
      cwd,
      { org: grid, auto: opts.auto, name: opts.name },
      {
        cliVersion: PKG_VERSION,
        writer: deps.writer
          ? (s: string) => deps.writer!(s)
          : undefined,
        isTTY: deps.tty,
        ...(deps.autoInitDeps ?? {}),
      },
    );
    if (result.kind === 'declined') return;
    link = result.link;
  }

  // `--grid <slug>` (backlog 308) overrides the persisted active grid for the
  // mismatch check. The grid list lets the mismatch message display the
  // link's *current* canonical slug rather than a stale stored one.
  const { activeOrg, orgs } = await getActiveOrgWithList(baseClient, {
    override: grid,
  });

  // Spec §5: link-vs-active-org mismatch is a hard stop. This is the 320.C
  // preflight: it compares by org_id and fires before any deploy submission,
  // so a link in a different org than the effective `--org`/active org errors
  // here with no wasted round-trip.
  validateLinkVsActiveOrg(link, activeOrg, orgs);

  // Backlog 320 — rebind the client to the *resolved* org so the deploy
  // submission (and every downstream request) carries the effective org's
  // slug as `x-cloudgrid-org`, not the slug the client was first built with.
  // Rebinding to `activeOrg.slug` (the server-canonical slug) also corrects a
  // stale legacy slug — e.g. a persisted `_system_` becomes `system`.
  const linkedClient = baseClient.withOrgSlug(activeOrg.slug);

  // --no-deploy in a linked dir: documented as a no-op success. The entity
  // stays at whatever status the server has it in (often 'live' on a
  // linked dir; charged on a freshly-init'd one). No /plug call fires.
  if (opts.noDeploy) {
    if (opts.json) {
      process.stdout.write(
        JSON.stringify({ status: 'charged', entity_id: link.entity_id }) +
          '\n',
      );
    } else {
      process.stdout.write(
        `${link.org_slug}: ${link.entity_id} already charged. No deploy queued.\n`,
      );
    }
    return;
  }

  return runLinkedDirPlug(linkedClient, link, cwd, opts, deps);
}

/**
 * Shared post-link plug flow — both the regular linked-dir path and the
 * `--existing`-then-plug path will land here once a LinkArtifact is in hand.
 */

async function maybeRunEnvPreflight(
  client: IV2Client,
  link: LinkArtifact,
  cwd: string,
  yaml: CloudgridYaml,
  opts: PlugOpts,
  deps: PlugDeps,
): Promise<void> {
  const services = (yaml.services ?? {}) as Record<string, unknown>;
  const declared = collectDeclaredEnvKeys(cwd, Object.keys(services));
  if (declared.length === 0) return;

  const writer = deps.writer ?? ((s: string) => process.stdout.write(s));
  const isTTY =
    deps.tty ?? Boolean(process.stdout.isTTY && process.stdin.isTTY);
  const confirm =
    deps.preflightConfirm ??
    (async (message: string, defaultValue = false) => {
      const ans = await inquirer.prompt([
        { type: 'confirm', name: 'ok', message, default: defaultValue },
      ]);
      return Boolean(ans.ok);
    });
  const askInput =
    deps.preflightInput ??
    (async (message: string, askOpts?: { hidden?: boolean }) => {
      const ans = await inquirer.prompt([
        askOpts?.hidden
          ? { type: 'password', name: 'value', message, mask: '*' }
          : { type: 'input', name: 'value', message },
      ]);
      return String(ans.value ?? '');
    });

  const outcome = await runEnvPreflight({
    client,
    entityId: link.entity_id,
    declared,
    forceMissingEnv: !!opts.forceMissingEnv,
    deps: { isTTY, confirm, askInput, writer },
  });
  if (outcome.action === 'abort') {
    throw new CloudGridError('Plug aborted: missing env keys.');
  }
}

/**
 * Backlog 015-B — client-side TypeScript preflight. For each service whose
 * source dir ships a `tsconfig.json`, run `tsc --noEmit` before the tarball is
 * built. On type errors, print them and abort with a CloudGridError so the
 * deploy never reaches Cloud Build. The shape mirrors `maybeRunEnvPreflight`:
 *   - injectable runner + writer for tests,
 *   - no-op when no service has a tsconfig (zero added latency for non-TS apps),
 *   - bypassed entirely by `--skip-preflight`.
 * Progress + error detail go to the writer (stderr by default) so `--json`
 * stdout stays clean.
 */
async function maybeRunTsPreflight(
  cwd: string,
  yaml: CloudgridYaml,
  opts: PlugOpts,
  deps: PlugDeps,
): Promise<void> {
  // --skip-preflight bypasses every client-side preflight, including this one.
  if (opts.skipPreflight) return;

  const services = (yaml.services ?? {}) as Record<string, unknown>;
  const serviceDirs: TsServiceTarget[] = [];
  for (const svcName of Object.keys(services)) {
    // HTTP-mode cron services have no source dir to type-check (backlog 076).
    if (isHttpModeCron(services[svcName])) continue;
    const dir = resolveServiceSourceDir({
      serviceConfig: services[svcName],
      serviceName: svcName,
      projectRoot: cwd,
    });
    serviceDirs.push({ service: svcName, dir });
  }

  const targets = detectTsServices({ serviceDirs });
  if (targets.length === 0) return; // non-TS app — nothing to check

  const writer = deps.writer ?? ((s: string) => process.stderr.write(s));
  const runCheck = deps.tsCheck ?? defaultTsCheckRunner;

  const outcome = await runTsPreflight({ targets, runCheck, writer });
  if (outcome.action === 'fail') {
    for (const f of outcome.failures) {
      writer(`\nType errors in ${f.service}:\n${f.output}\n`);
    }
    const names = outcome.failures.map((f) => f.service).join(', ');
    throw new CloudGridError(
      `Preflight failed: tsc --noEmit found type errors in ${names}. ` +
        'Fix them, or re-run with --skip-preflight to deploy anyway.',
    );
  }
}

/**
 * Backlog 091 Layer 3 — pre-upload warning for entities still on the
 * pre-010 namespace layout. The shape mirrors `maybeRunEnvPreflight`:
 *   - injectable confirm + writer + tty for tests,
 *   - no-op when not relevant,
 *   - throws CloudGridError on user decline.
 */
async function maybeRunLegacyPreCutoverPreflight(
  client: IV2Client,
  link: LinkArtifact,
  opts: PlugOpts,
  deps: PlugDeps,
): Promise<void> {
  if (opts.skipPreflight) return;
  // The user already opted into the cutover — no warning to surface.
  if (opts.migrateFromLegacy !== undefined) return;

  let runtime: { legacy_pre_cutover?: boolean } | undefined;
  try {
    runtime = await client.get<{ legacy_pre_cutover?: boolean }>(
      `/api/v2/runtimes/${link.entity_id}`,
    );
  } catch {
    // Best-effort. A failure here (network, 5xx, mock not registered) must
    // not block the deploy — the existing /plug call is the authoritative
    // gate, and it'll emit a real error if the entity is unreachable.
    return;
  }

  if (!runtime || runtime.legacy_pre_cutover !== true) return;

  const writer = deps.writer ?? ((s: string) => process.stdout.write(s));
  const isTTY =
    deps.tty ?? Boolean(process.stdout.isTTY && process.stdin.isTTY);

  const lines = [
    `Warning: This entity (${link.entity_id}) was first plugged before the decision 010 namespace cutover.`,
    'Re-deploying via the v0.8 plug pipeline may fail at apply_manifests because the legacy',
    "namespace still claims this entity's ingress hostname.",
    '',
    'Pick one:',
    '  1. Continue (the failure mode is loud, not silent — pods still deploy into the v0.8 namespace, just with no Ingress).',
    '  2. Run `cloudgrid plug --migrate-from-legacy <legacy-namespace>` first',
    '     (one-time cutover; documented at docs/operations/2026-05-07-legacy-pre-cutover-migration.md).',
    "  3. Don't re-plug — keep maintaining the legacy app via direct image tag updates.",
    '',
  ];
  for (const line of lines) {
    writer(line + '\n');
  }

  // Non-TTY (CI) and `--json` callers can't answer a prompt — surface the
  // warning and proceed. The /plug call's own response will surface the
  // Ingress-conflict error if the cutover hasn't happened yet.
  if (!isTTY || opts.json) return;

  const confirm =
    deps.preflightConfirm ??
    (async (message: string, defaultValue = false) => {
      const ans = await inquirer.prompt([
        { type: 'confirm', name: 'ok', message, default: defaultValue },
      ]);
      return Boolean(ans.ok);
    });

  const ok = await confirm('Continue with plug?', true);
  if (!ok) {
    throw new CloudGridError(
      'Plug aborted: legacy_pre_cutover entity. Run `cloudgrid plug --migrate-from-legacy <ns>` to migrate.',
    );
  }
}

function isHttpModeCron(svc: unknown): boolean {
  if (!svc || typeof svc !== 'object') return false;
  const s = svc as { type?: unknown; run?: unknown };
  if (s.type !== 'cron') return false;
  return typeof s.run === 'string'
    && (s.run.startsWith('http://') || s.run.startsWith('https://'));
}

// Backlog 091 Layer 2 — DNS-1123 namespace name (lowercase alphanumeric +
// hyphens, max 63, must start + end alphanumeric). The server re-validates;
// the CLI check is purely so a typo aborts before the tarball upload.
const LEGACY_NAMESPACE_RE = /^[a-z0-9]([-a-z0-9]{0,61}[a-z0-9])?$/;

async function runLinkedDirPlug(
  client: IV2Client,
  link: LinkArtifact,
  cwd: string,
  opts: PlugOpts,
  deps: PlugDeps,
): Promise<void> {
  // Backlog 091 Layer 2 — fail fast on a malformed `--migrate-from-legacy`
  // value before we build a tarball or hit the server. Trim and validate
  // against the DNS-1123 label rule.
  let migrateFromLegacy: string | undefined;
  if (opts.migrateFromLegacy !== undefined) {
    const trimmed = opts.migrateFromLegacy.trim();
    if (trimmed.length === 0 || !LEGACY_NAMESPACE_RE.test(trimmed)) {
      throw new CloudGridError(
        `--migrate-from-legacy '${opts.migrateFromLegacy}' is not a valid K8s namespace name (lowercase alphanumeric + hyphens, max 63 chars).`,
      );
    }
    migrateFromLegacy = trimmed;
  }

  // Backlog 091 Layer 3 — preflight warning for `legacy_pre_cutover` entities.
  // Pre-010 entities still claim their hostname via the legacy named namespace,
  // so a v0.8 plug fails at apply_manifests with the Ingress conflict from
  // backlog 091. The check fires BEFORE the (expensive) tarball build and is
  // a no-op when:
  //   - the user passed `--skip-preflight` (CI opt-out),
  //   - the user passed `--migrate-from-legacy <ns>` (already aware), or
  //   - the entity is not flagged `legacy_pre_cutover`.
  // In TTY: prompts with a 3-option block; aborts cleanly on `n`.
  // In non-TTY (or `--json`): prints the warning and proceeds.
  await maybeRunLegacyPreCutoverPreflight(client, link, opts, deps);

  const { text: yamlText, parsed: yaml } = loadCloudgridYaml(cwd);

  // Backlog 082 + 086 — client-side preflight. Same routability + service
  // type↔folder alignment checks the server runs in `_deployForEntity`;
  // failing here saves the round-trip + tarball build for yamls that would
  // be rejected. The kind comes from `.cloudgrid/link.json` (decision 012 —
  // yaml is shape-only). The folder reader factory is fs-based; it returns
  // null when a service folder is absent (e.g. a HTTP-mode-cron-only project)
  // so alignment skips silently rather than firing a false positive.
  // Soft-warning channel for `validateConfig`. Carries deprecation
  // notices (e.g. `network.public` → `expose` migration, backlog 191;
  // `domain:` → `custom_domains:`, backlog 104) and the path-absent
  // suggestion. Backlog 212 phase 2 (0.8.15) moved strict-keys unknown
  // fields to die(); they no longer flow through this channel. Default
  // sink is stderr so warnings are visible before the build kicks off
  // without polluting `--json` machine output on stdout. Tests inject
  // an array-collecting stub via `deps.validateWarn`.
  const validateWarn =
    deps.validateWarn ??
    ((message: string) => {
      process.stderr.write(`${message}\n\n`);
    });
  try {
    const { validateConfig } = await import(
      '../../../shared/src/validate.js'
    );
    const { makeFsServiceReaderFactory } = await import(
      '../services/service-folder-reader.js'
    );
    validateConfig(yaml as any, {
      kind: link.kind === 'agent' ? 'agent' : 'app',
      serviceReader: makeFsServiceReaderFactory(cwd),
      warn: validateWarn,
    });
  } catch (err) {
    throw new CloudGridError((err as Error).message);
  }

  // Backlog 073 — pre-upload `.env.example` validation. Runs only when at
  // least one service ships a `.env.example`; otherwise no-op. Aborts with
  // a CloudGridError before any tarball is built when keys are missing and
  // the user (or CI flag) has not opted to proceed.
  await maybeRunEnvPreflight(client, link, cwd, yaml, opts, deps);

  // Backlog 015-B — TypeScript preflight. For every service that ships a
  // tsconfig.json, run `tsc --noEmit` locally before building the tarball.
  // A production-only type error (the `cloudgrid dev` vs `next build` parity
  // gap) is caught in seconds here instead of after a 2-minute Cloud Build.
  // No-op for non-TS apps (zero added latency); bypassed by --skip-preflight.
  await maybeRunTsPreflight(cwd, yaml, opts, deps);

  // Build the multipart form. Blob + FormData are global in Node 22.
  const form = new FormData();
  form.append(
    'cloudgrid.yaml',
    new Blob([yamlText], { type: 'text/yaml' }),
    'cloudgrid.yaml',
  );

  const services = (yaml.services ?? {}) as Record<string, unknown>;
  for (const svcName of Object.keys(services)) {
    // Backlog 076: HTTP-mode cron services have no source — the
    // CronJob curl image runs against an external URL — so there is
    // no services/<svc>/ folder to package. Skip the tarball entirely.
    if (isHttpModeCron(services[svcName])) continue;

    // Backlog 108 — when the service declares `build:` in yaml, the build
    // output dir (default `dist`, overridable via `build.output`) is
    // produced server-side by Cloud Build. Pass it to the tarball builder
    // so it's excluded from the upload. Static services without `build:`
    // pass undefined and keep their pre-built `dist/` (or whatever folder
    // they're serving) intact in the tarball.
    const svcAny = services[svcName] as { build?: { output?: string } } | undefined;
    const buildOutputDir = svcAny?.build
      ? svcAny.build.output ?? 'dist'
      : undefined;

    // Backlog 124 — `services.<svc>.source.path` (when declared) overrides the
    // legacy `<projectRoot>/services/<svc>/` convention so multi-service repos
    // with sources in subdirs (e.g. `./api`, `./web`) work without forcing
    // the boilerplate layout. Falls back to `services/<svcName>` when absent.
    const serviceDir = resolveServiceSourceDir({
      serviceConfig: services[svcName],
      serviceName: svcName,
      projectRoot: cwd,
    });
    const tarball = buildServiceTarball({ serviceDir, projectRoot: cwd, buildOutputDir });
    // `Buffer<ArrayBufferLike>` is not assignable to BlobPart under the
    // current lib settings (BlobPart requires the stricter `ArrayBuffer`).
    // Copying into a fresh ArrayBuffer eliminates the variance issue and
    // is cheap relative to the upload itself.
    const tarBytes = new Uint8Array(tarball.byteLength);
    tarBytes.set(tarball);
    form.append(
      svcName,
      new Blob([tarBytes.buffer], { type: 'application/gzip' }),
      `${svcName}.tar.gz`,
    );
  }

  for (const inspId of opts.inspiredBy ?? []) {
    form.append('inspired_by', inspId);
  }
  if (opts.displayName) {
    form.append('display_name', opts.displayName);
  }
  // Backlog 091 Layer 2 — one-time cutover from a pre-010 named namespace.
  // The deployer's `legacy_cutover` step runs only when this field arrives.
  if (migrateFromLegacy) {
    form.append('migrate_from_legacy_namespace', migrateFromLegacy);
  }

  // POST /api/v2/entities/:id/plug.
  let result: PlugResult;
  try {
    result = await client.plug(link.entity_id, form, {
      cliVersion: PKG_VERSION,
    });
  } catch (err) {
    if (err instanceof V2ApiError) {
      // BL595 — a maintenance 503 is not a deploy error: let it reach the
      // top-level handleV2Error, which renders the friendly message plain and
      // skips telemetry. mapPlugServerError would otherwise flatten it into a
      // generic "Server error (503 …)" CloudGridError with a stack-style print.
      if (err.code === 'PLUG_MAINTENANCE') throw err;
      throw mapPlugServerError(err, { entityIdOrSlug: link.entity_id });
    }
    throw err;
  }

  // Resolve the trace_id we'll poll on.
  let traceId: string;
  if (result.kind === 'queued') {
    traceId = result.trace_id;
  } else {
    if (!result.trace_id) {
      throw new CloudGridError(
        'Server reported an in-flight deploy for this entity but did not include a trace id to attach to.',
      );
    }
    traceId = result.trace_id;
  }

  const yamlSlug = typeof yaml.name === 'string' ? yaml.name : link.entity_id;
  const warnings: PlugWarning[] = result.warnings ?? [];

  // BL659 — the server's canonical serving URL, composed once server-side by
  // `composeEntityServingUrl` (`entityUrl()` + the per-grid flat-arch flag +
  // the system-org apex special case, #1438). Per Decision 007, entity URLs
  // come from ONE function — so the CLI consumes this rather than recomposing.
  // The deploy trace's `result.url` is the raw `entityUrl()` primitive, which
  // 404s for flat-arch grids and system-apex entities; it stays only as the
  // fallback for the (rare) case where the server hit a composition hiccup and
  // omitted the field. `''` normalizes an absent/blank value to the falsy
  // sentinel the outlet call-sites already treat as "no URL".
  const serverUrl = result.url ?? '';

  const now = deps.now ?? Date.now;
  const startedAtMs = now();
  const buildLogTailFor = deps.buildLogTail ?? defaultBuildLogTailFor;
  const runtimeErrorTailFor = deps.runtimeErrorTail ?? defaultRuntimeErrorTailFor;
  const notify = deps.notify ?? defaultOsNotify;
  const appendDeployLog = deps.appendDeployLog ?? defaultAppendDeployLog;
  const writeOut = deps.writer ?? ((s: string) => process.stdout.write(s));

  // Backlog 544 — resolve the effective poll budget. `--timeout <minutes>`
  // wins when valid; otherwise the test-injection dep; otherwise undefined so
  // `pollDeploy` falls back to its 20-minute `DEFAULT_MAX_DURATION_MS`.
  const maxDurationMs = parseTimeoutMinutes(opts.timeout) ?? deps.maxDurationMs;

  // ── --detach (072-A): hand the trace off to a background watcher and exit. ──
  if (opts.detach) {
    const spawnFn = deps.spawnWatcher ?? defaultSpawnDetachedWatcher;
    let spawnResult: SpawnResult = { ok: false };
    try {
      spawnResult = spawnFn({ traceId });
    } catch {
      spawnResult = { ok: false };
    }

    if (opts.json) {
      process.stdout.write(
        JSON.stringify({
          status: 'queued',
          entity_id: link.entity_id,
          trace_id: traceId,
          detached: spawnResult.ok,
          ...(warnings.length > 0 ? { warnings } : {}),
        }) + '\n',
      );
      return;
    }

    for (const w of warnings) {
      writeOut(`Warning: ${w.message}\n`);
    }

    if (spawnResult.ok) {
      writeOut(
        `✓ Deploy queued (trace ${traceId}). A background watcher will send a system notification when done.\n`,
      );
    } else {
      writeOut(
        `✓ Deploy queued (trace ${traceId}).\n` +
          `  Background watcher couldn't start. Check progress with: cloudgrid status ${traceId}\n`,
      );
    }
    return;
  }

  // ── BL567 queued plug path ──
  // When the 202 carried a `task_id`, the deploy was enqueued on the BL566 task
  // substrate (durable across pod restarts). Poll `GET /api/v2/tasks/<id>` for
  // execution state and render the same step UX. The in-process trace path
  // below is unchanged and still serves every non-allowlisted entity (202 with
  // no `task_id`) byte-for-byte. `--detach` (above) already handed the deploy
  // to the trace watcher via `traceId`, which works for queued too — so we only
  // reach here for the foreground (human + --json) render.
  const queuedTaskId =
    result.kind === 'queued' && result.task_id ? result.task_id : undefined;
  if (result.kind === 'queued' && result.queued === true && !result.task_id) {
    // Server flagged the queued path but gave us no id to poll — a server bug.
    // Don't silently fall through to the trace path; surface it so it gets
    // reported, and hand the user the status command as a fallback.
    throw new CloudGridError(
      'Server reported a queued plug but did not return a task id to poll. ' +
        `Please report this. You can check progress with \`cloudgrid status ${yamlSlug}\`.`,
    );
  }
  if (queuedTaskId) {
    return runQueuedTaskPlug({
      client,
      taskId: queuedTaskId,
      traceId,
      entityId: link.entity_id,
      orgSlug: link.org_slug,
      yamlSlug,
      warnings,
      // BL659 — hand the plug response's canonical URL to the queued path so
      // its outlet matches the in-process path (and `info`/`status`).
      serverUrl,
      opts,
      deps,
      now,
      startedAtMs,
      maxDurationMs,
      notify,
      appendDeployLog,
    });
  }

  // ── --json: emit a single line on stdout, suppress all renderer chrome. ──
  if (opts.json) {
    let final: DeployTrace;
    try {
      final = await pollDeploy(client, traceId, {
        pollIntervalMs: deps.pollIntervalMs,
        maxDurationMs,
        sleep: deps.sleep,
      });
    } catch (err) {
      if (err instanceof V2ApiError) {
        throw mapPlugServerError(err, { entityIdOrSlug: link.entity_id });
      }
      throw err;
    }
    // BL659 — prefer the server's canonical serving URL from the plug
    // response; fall back to the deploy trace's raw-primitive `result.url`
    // only when the server omitted it (composition hiccup).
    const outlet = serverUrl || (final.result?.url ?? '');
    const buildId =
      (final.result as { build_id?: string } | null)?.build_id ??
      resolveBuildId(final).buildId;
    process.stdout.write(
      JSON.stringify({
        status: final.status,
        entity_id: link.entity_id,
        trace_id: traceId,
        outlet,
        build_id: buildId,
        ...(warnings.length > 0 ? { warnings } : {}),
      }) + '\n',
    );
    await recordTerminalState({
      final,
      yamlSlug,
      elapsedMs: now() - startedAtMs,
      notifyEnabled: shouldNotify(opts),
      notify,
      appendDeployLog,
    });
    if (final.status !== 'success') {
      // Backlog 187 — JSON mode still throws the user-language message
      // up to the top-level handler so the shell's stderr line is
      // actionable. The JSON payload above carries the full trace; if
      // the consumer wants the structured error, it's in
      // `final.error` exactly as the API returned it.
      throw new CloudGridError(
        pickErrorMessage(final.error) ??
          `Deploy failed (${final.error?.code ?? 'UNKNOWN'}).`,
      );
    }
    return;
  }

  // ── Human mode: drive the multi-step renderer. ──
  const headerLine =
    result.kind === 'attached'
      ? `↻ Attaching to in-flight build for ${yamlSlug}...`
      : `Plugging ${yamlSlug} into ${link.org_slug}-hub...`;

  // Server-side warnings (e.g. user-supplied Dockerfile stripped per
  // decision 013) print before the multi-step renderer starts so they
  // aren't trampled by transient redraws. One line per warning, prefixed
  // with `Warning:` — voice rules: plain, no emoji, no exclamation marks.
  for (const w of warnings) {
    writeOut(`Warning: ${w.message}\n`);
  }

  // Backlog 071-A — Cloud Build progress under the cloud_build spinner.
  // Default on; opt out via --no-progress (e.g. CI). Renderer-only chrome,
  // so we skip the tracker entirely in CI mode (tty=false) where extras
  // wouldn't render anyway. We only build the tracker once cloud_build
  // actually transitions to in_progress.
  const isTty = deps.tty ?? Boolean(process.stdout.isTTY);
  const progressEnabled =
    isTty && opts.noProgress !== true && opts.progress !== false;
  const trackerFactory = deps.buildProgressTracker ?? (() => new BuildProgressTracker());
  // Holder so closures (onPoll) can mutate without TS re-narrowing the
  // outer `tracker` to `null` past the catch block.
  const trackerRef: { current: BuildProgressTracker | null } = { current: null };
  let cloudBuildStarted = false;
  let cloudBuildEnded = false;

  const renderer = new MultiStepRenderer({
    tty: deps.tty,
    writer: deps.writer,
    getStepExtras: progressEnabled
      ? () => trackerRef.current?.getMessages() ?? {}
      : undefined,
  });
  renderer.start(headerLine);

  let final: DeployTrace;
  try {
    final = await pollDeploy(client, traceId, {
      pollIntervalMs: deps.pollIntervalMs,
      maxDurationMs,
      sleep: deps.sleep,
      onPoll: (trace) => {
        if (progressEnabled) {
          const cb = trace.steps.find((s) => s.step === 'cloud_build');
          if (cb && !cloudBuildStarted && cb.status === 'in_progress') {
            cloudBuildStarted = true;
            trackerRef.current = trackerFactory();
            // Fire-and-forget — start() does its own discovery + polling.
            // Errors are swallowed inside the tracker; never block the deploy.
            void trackerRef.current.start(yamlSlug);
          }
          if (
            cb && cloudBuildStarted && !cloudBuildEnded && cb.status !== 'in_progress'
          ) {
            cloudBuildEnded = true;
            trackerRef.current?.stop();
          }
        }
        renderer.update(trace.steps);
      },
    });
  } catch (err) {
    trackerRef.current?.stop();
    renderer.finish();
    if (err instanceof V2ApiError) {
      throw mapPlugServerError(err, { entityIdOrSlug: link.entity_id });
    }
    throw err;
  }
  trackerRef.current?.stop();

  const elapsedMs = now() - startedAtMs;

  if (final.status === 'success') {
    // BL659 — prefer the server's canonical serving URL; fall back to the
    // deploy trace's raw-primitive `result.url` only if the server omitted it.
    const outlet = serverUrl || (final.result?.url ?? '');
    // Make the speed promise visible at the climax of the first plug: a real
    // elapsed reading next to "Live" is the "live in 60 seconds" claim being
    // kept, not advertised. Sub-second / unreadable timings fall back to a
    // bare "Live." so we never print "Live in 0s." §23: plain, confident.
    const elapsed = formatElapsedHuman(elapsedMs);
    const liveLine =
      elapsed && elapsed !== '0s' && elapsed !== '-'
        ? `✓ Live in ${elapsed}.`
        : '✓ Live.';
    // Smoke Finding 2 — surface visibility + a make-shareable tip so the user
    // isn't blindsided by a URL that 302s anonymous visitors to login. The URL
    // itself is the `Outlet:` line below. Best-effort: a failed fetch leaves
    // the block empty and never blocks the already-successful plug.
    const visibilityBlock = await fetchPlugVisibilityBlock(
      client,
      link.entity_id,
      yamlSlug,
    );
    const finalLines = [liveLine];
    if (outlet) finalLines.push(`  Outlet: ${outlet}`);
    if (visibilityBlock) {
      // QA-13 — frame the share affordance with a blank line above and below so
      // it reads as a distinct block under the URL, not a run-on of the Outlet
      // line. This is the moment the user is about to share; the block must be
      // easy to spot.
      finalLines.push('', visibilityBlock, '');
    }
    renderer.finish(finalLines.join('\n'));
    await recordTerminalState({
      final,
      yamlSlug,
      elapsedMs,
      notifyEnabled: shouldNotify(opts),
      notify,
      appendDeployLog,
    });
    return;
  }

  // Terminal failed.
  renderer.finish();

  // Backlog 187 — when the server returned the structured DeployEventError,
  // the user-language `message_user` + suggested fixes are already
  // translated. Render that as the primary surface; the legacy 099 Phase 2
  // step-classifier renderer kicks in only for events without the
  // structured shape (pre-187 events, or routes that haven't been updated).
  // The technical detail (build-log tail, runtime-error tail) stays
  // visible only under --verbose so a casual user sees actionable
  // guidance, not a stack trace.
  const verbose = opts.verbose === true;
  if (hasStructuredDeployError(final) && final.error) {
    writeOut(
      renderStructuredDeployError(final.error, {
        entitySlug: yamlSlug,
        verbose,
      }) + '\n',
    );
  } else {
    // Backlog 099 Phase 2 — beautified terminal-state message. Prints
    // between the multi-step renderer's failed step and the build/runtime
    // tails so the headline + remediation are the most prominent thing on
    // the screen, with the technical detail beneath. The renderer is
    // pure: same trace + slug always produces the same output.
    writeOut(renderTerminalState(final, yamlSlug) + '\n');
  }

  // Backlog 187 — the build-log + runtime-error tails carry K8s-flavored
  // technical detail. Behind --verbose only (or when the structured error
  // shape was not available, so the legacy renderer is the only thing the
  // user sees). The headline + suggested fixes above are the actionable
  // surface; the tails are diagnostic depth for an engineer.
  const showTails = verbose || !hasStructuredDeployError(final);
  if (showTails) {
    // Backlog 071-A — best-effort Cloud Build log tail under the failed
    // step. Always emitted on cloud_build failure when we can resolve a
    // build_id. Falls back to a click-through URL when gcloud is
    // unavailable.
    //
    // Backlog 292 — skip this legacy local-`gcloud` tail when the structured
    // error already carried a server-captured `build_log_excerpt` with text:
    // `renderStructuredDeployError` has already printed the authoritative
    // tail above, so re-running the local fetch would just double-render it.
    const serverExcerptText = (
      final.error as { build_log_excerpt?: { text?: string } } | null
    )?.build_log_excerpt?.text;
    const alreadyShownServerTail =
      hasStructuredDeployError(final) &&
      typeof serverExcerptText === 'string' &&
      serverExcerptText.length > 0;
    if (!alreadyShownServerTail) {
      try {
        const tail = await buildLogTailFor(final);
        if (tail) writeOut(tail + '\n');
      } catch {
        // Never block the failure surface on a tail-fetch hiccup.
      }
    }

    // Backlog 071-B — typed runtime-error tail under wait_rollout
    // failures. Renders inline stderr, the registry message, or the
    // memory hint. Synchronous render — the structured payload is
    // already on the trace; no extra round-trips.
    try {
      const tail = runtimeErrorTailFor(final);
      if (tail) writeOut(tail + '\n');
    } catch {
      // Same defensive posture as the build-log tail above.
    }
  }

  await recordTerminalState({
    final,
    yamlSlug,
    elapsedMs,
    notifyEnabled: shouldNotify(opts),
    notify,
    appendDeployLog,
  });

  const deployError = new CloudGridError(
    pickErrorMessage(final.error) ??
      `Deploy failed (${final.error?.code ?? 'UNKNOWN'}).`,
  );
  // BL593 — attach the trace id so the top-level catch's cli_events row
  // carries which in-process deploy failed (`metadata.trace_id`). The queued
  // path's task_id is attached on PlugTaskFailedError itself.
  (deployError as { _telemetryMetadata?: Record<string, unknown> })._telemetryMetadata = {
    trace_id: traceId,
  };
  throw deployError;
}

// ─────────────────────────────────────────────────────────────────────────────
// BL567 — queued plug rendering
// ─────────────────────────────────────────────────────────────────────────────

interface QueuedTaskPlugArgs {
  client: IV2Client;
  taskId: string;
  /** Carried for the soft-timeout wording (and parity with the trace path). */
  traceId: string;
  entityId: string;
  orgSlug: string;
  yamlSlug: string;
  warnings: PlugWarning[];
  /**
   * BL659 — the server's canonical serving URL from the plug response, or ''
   * when the server omitted it. Preferred over the `/api/v2/runtimes/:id`
   * fallback so the queued path's outlet matches the in-process path.
   */
  serverUrl: string;
  opts: PlugOpts;
  deps: PlugDeps;
  now: () => number;
  startedAtMs: number;
  maxDurationMs: number | undefined;
  notify: typeof defaultOsNotify;
  appendDeployLog: typeof defaultAppendDeployLog;
}

/**
 * BL567 — drive a queued plug to its terminal state by polling
 * `GET /api/v2/tasks/<id>`, rendering the same step UX as the in-process flow.
 * Handles both `--json` (single machine line) and the human multi-step render.
 * `pollPlugTask` throws on failure / soft-timeout / network loss; those flow
 * up to the top-level handler exactly like the trace path's errors.
 */
async function runQueuedTaskPlug(args: QueuedTaskPlugArgs): Promise<void> {
  const {
    client,
    taskId,
    traceId,
    entityId,
    orgSlug,
    yamlSlug,
    warnings,
    serverUrl,
    opts,
    deps,
    now,
    startedAtMs,
    maxDurationMs,
    notify,
    appendDeployLog,
  } = args;
  const writeOut = deps.writer ?? ((s: string) => process.stdout.write(s));

  // ── --json: one line on stdout, no renderer chrome. ──
  if (opts.json) {
    let final: PlugTaskResult;
    try {
      final = await pollPlugTask(taskId, client, { update: () => {} }, {
        intervalMs: deps.pollIntervalMs,
        timeoutMs: maxDurationMs,
        slug: yamlSlug,
        traceId,
        sleep: deps.sleep,
        now,
      });
    } catch (err) {
      if (err instanceof V2ApiError) {
        throw mapPlugServerError(err, { entityIdOrSlug: entityId });
      }
      throw err;
    }
    // BL659 — prefer the plug response's canonical URL; only fall back to the
    // `/api/v2/runtimes/:id` detail when the server omitted it.
    const outletUrl =
      serverUrl || (await fetchQueuedOutlet(client, entityId)).url || '';
    process.stdout.write(
      JSON.stringify({
        status: 'success',
        entity_id: entityId,
        task_id: taskId,
        trace_id: traceId,
        outlet: outletUrl,
        ...(final.status === 'cleaned_up' ? { cleaned_up: true } : {}),
        ...(warnings.length > 0 ? { warnings } : {}),
      }) + '\n',
    );
    await recordQueuedTerminalState({
      yamlSlug,
      elapsedMs: now() - startedAtMs,
      success: true,
      notifyEnabled: shouldNotify(opts),
      notify,
      appendDeployLog,
    });
    return;
  }

  // ── Human mode: drive the multi-step renderer (identical to the in-process
  // flow's header + rows + final block). ──
  for (const w of warnings) {
    writeOut(`Warning: ${w.message}\n`);
  }

  const renderer = new MultiStepRenderer({ tty: deps.tty, writer: deps.writer });
  renderer.start(`Plugging ${yamlSlug} into ${orgSlug}-hub...`);

  let final: PlugTaskResult;
  try {
    final = await pollPlugTask(taskId, client, renderer, {
      intervalMs: deps.pollIntervalMs,
      timeoutMs: maxDurationMs,
      slug: yamlSlug,
      traceId,
      sleep: deps.sleep,
      now,
      // BL595 — print a friendly one-line update the first time the queued task
      // reports a maintenance window; pollPlugTask then slows its own cadence.
      onMaintenance: (m) =>
        writeOut(
          `\nCloudGrid is briefly paused for maintenance — your plug is queued and will resume shortly.\n  ${m.message}\n`,
        ),
    });
  } catch (err) {
    renderer.finish();
    // Record a failure line (best-effort) for a genuine terminal failure so the
    // queued path leaves the same deploys.log trail the in-process path does.
    // Timeouts / network loss are not deploy failures — they stay unrecorded.
    if (err instanceof PlugTaskFailedError) {
      await recordQueuedTerminalState({
        yamlSlug,
        elapsedMs: now() - startedAtMs,
        success: false,
        notifyEnabled: shouldNotify(opts),
        notify,
        appendDeployLog,
      });
    }
    if (err instanceof V2ApiError) {
      throw mapPlugServerError(err, { entityIdOrSlug: entityId });
    }
    throw err;
  }

  const elapsedMs = now() - startedAtMs;
  const elapsed = formatElapsedHuman(elapsedMs);
  const liveLine =
    elapsed && elapsed !== '0s' && elapsed !== '-'
      ? `✓ Live in ${elapsed}.`
      : '✓ Live.';

  // BL659 — the URL comes from the plug response's canonical `serverUrl`; the
  // runtimes fetch still supplies the visibility block (and the URL fallback
  // when the server omitted it).
  const outlet = await fetchQueuedOutlet(client, entityId);
  const outletUrl = serverUrl || outlet.url || '';
  const finalLines = [liveLine];
  if (outletUrl) finalLines.push(`  Outlet: ${outletUrl}`);
  if (outlet.visibility) {
    // QA-13 — same share-affordance block + blank-line framing as the
    // in-process success path.
    const block = renderPlugVisibilityBlock(outlet.visibility, yamlSlug).join('\n');
    finalLines.push('', block, '');
  }
  renderer.finish(finalLines.join('\n'));

  await recordQueuedTerminalState({
    yamlSlug,
    elapsedMs,
    success: true,
    notifyEnabled: shouldNotify(opts),
    notify,
    appendDeployLog,
  });
}

/**
 * BL567 — fetch the entity's serving URL + visibility for the queued success
 * block. The URL lives on the entity runtime (the task's `result` carries
 * build/deploy handles, not the public URL), so we read it from the same
 * `/api/v2/runtimes/:id` detail the in-process visibility block uses. Best-
 * effort: the deploy already succeeded, so a fetch hiccup returns `{}` rather
 * than failing the plug.
 */
async function fetchQueuedOutlet(
  client: IV2Client,
  entityId: string,
): Promise<{ url?: string; visibility?: string }> {
  try {
    const detail = await client.get<{ url?: string; visibility?: string }>(
      `/api/v2/runtimes/${encodeURIComponent(entityId)}`,
    );
    return { url: detail?.url, visibility: detail?.visibility };
  } catch {
    return {};
  }
}

/**
 * BL567 — append the deploys.log line + fire the OS notification for a queued
 * deploy. The task surface doesn't carry a failed-step name or a build id (those
 * live on the deploy event), so the log entry omits them. Both side effects are
 * best-effort.
 */
async function recordQueuedTerminalState(opts: {
  yamlSlug: string;
  elapsedMs: number;
  success: boolean;
  notifyEnabled: boolean;
  notify: typeof defaultOsNotify;
  appendDeployLog: typeof defaultAppendDeployLog;
}): Promise<void> {
  const entry: DeployLogEntry = {
    ts: new Date().toISOString(),
    slug: opts.yamlSlug,
    outcome: opts.success ? 'success' : 'failure',
    step: null,
    elapsedMs: opts.elapsedMs,
    buildId: null,
    errorCode: null,
  };
  opts.appendDeployLog(entry);

  if (opts.notifyEnabled) {
    const elapsed = formatElapsedHuman(opts.elapsedMs);
    const message = opts.success
      ? `✓ ${opts.yamlSlug} live in ${elapsed}`
      : `✗ ${opts.yamlSlug} deploy failed`;
    await opts.notify({ title: 'CloudGrid', message });
  }
}

/**
 * Backlog 187 — pick the best message for the exit-error string thrown to
 * the top-level handler. Prefers the user-language `message_user` from
 * the structured shape; falls back to the legacy `.message` (still
 * populated by the route shim for back-compat).
 */
function pickErrorMessage(
  err: { message_user?: string; message?: string } | null | undefined,
): string | undefined {
  if (!err) return undefined;
  if (typeof err.message_user === 'string' && err.message_user.length > 0) {
    return err.message_user;
  }
  if (typeof err.message === 'string' && err.message.length > 0) {
    return err.message;
  }
  return undefined;
}

// ─────────────────────────────────────────────────────────────────────────────
// Terminal-state side effects (072-C, 072-D)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Decide whether to fire an OS notification on terminal state. CLI flag
 * wins over config; config defaults to `true` when missing or unreadable.
 */
/**
 * Backlog 544 — parse `--timeout <minutes>` into milliseconds. Returns
 * `undefined` for an unset / unparseable / non-positive value so the caller
 * falls through to the default budget rather than a degenerate 0ms timeout.
 */
function parseTimeoutMinutes(raw: string | undefined): number | undefined {
  if (raw === undefined) return undefined;
  const minutes = Number(raw);
  if (!Number.isFinite(minutes) || minutes <= 0) return undefined;
  return Math.round(minutes * 60 * 1000);
}

function shouldNotify(opts: PlugOpts): boolean {
  // --no-notify always wins.
  if (opts.noNotify === true) return false;
  if (opts.notify === false) return false;

  // Config gate. Reading config is sync and cheap; treat any failure as
  // "config is silent" → default behaviour (on).
  try {
    if (!configExists()) return true;
    const cfg = loadConfig();
    return cfg.notify_on_deploy !== false;
  } catch {
    return true;
  }
}

/**
 * Smoke Finding 2 — build the post-plug visibility block: a one-line
 * description of the entity's visibility, plus a "make it shareable" tip when
 * an outside visitor can't yet reach it. New apps default to `org` visibility,
 * which silently 302s anonymous visitors to the login screen; the user needs
 * to be told and handed the one-step fix.
 *
 * Best-effort by design: the deploy already succeeded, so a hiccup fetching the
 * runtime detail returns '' rather than failing the plug. The runtime detail
 * carries `visibility` (services/runtimes-ops-v2.ts); we read only that field.
 */
async function fetchPlugVisibilityBlock(
  client: IV2Client,
  entityId: string,
  yamlSlug: string,
): Promise<string> {
  let visibility: string | undefined;
  try {
    const detail = await client.get<{ visibility?: string }>(
      `/api/v2/runtimes/${encodeURIComponent(entityId)}`,
    );
    visibility = detail?.visibility;
  } catch {
    return '';
  }
  if (!visibility) return '';
  // QA-13 — the visibility line plus a state-conditional CTA carrying the exact
  // `cloudgrid visibility set` command. Pure renderer so the copy is unit-
  // tested in one place (services/visibility-format.ts).
  return renderPlugVisibilityBlock(visibility, yamlSlug).join('\n');
}

/**
 * Append the deploys.log line and fire the OS notification. Both are
 * best-effort; any failure is swallowed inside the helpers.
 */
async function recordTerminalState(opts: {
  final: DeployTrace;
  yamlSlug: string;
  elapsedMs: number;
  notifyEnabled: boolean;
  notify: typeof defaultOsNotify;
  appendDeployLog: typeof defaultAppendDeployLog;
}): Promise<void> {
  const { final, yamlSlug, elapsedMs, notifyEnabled } = opts;
  const isSuccess = final.status === 'success';
  const failedStep = final.steps.find((s) => s.status === 'failed');
  const errorCode = final.error?.code ?? null;
  const buildId =
    (final.result as { build_id?: string } | null)?.build_id ??
    resolveBuildId(final).buildId;

  const entry: DeployLogEntry = {
    ts: new Date().toISOString(),
    slug: yamlSlug,
    outcome: isSuccess ? 'success' : 'failure',
    step: isSuccess ? null : failedStep?.step ?? final.error?.failed_step ?? null,
    elapsedMs,
    buildId,
    errorCode: isSuccess ? null : errorCode,
  };
  opts.appendDeployLog(entry);

  if (notifyEnabled) {
    const elapsed = formatElapsedHuman(elapsedMs);
    const message = isSuccess
      ? `✓ ${yamlSlug} live in ${elapsed}`
      : `✗ ${yamlSlug} deploy failed (${entry.step ?? 'unknown'}): ${
          errorCode ?? 'UNKNOWN'
        }`;
    await opts.notify({ title: 'CloudGrid', message });
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Commander surface
// ─────────────────────────────────────────────────────────────────────────────

function collectRepeatable(value: string, prev: string[]): string[] {
  return [...prev, value];
}

export const plugCommand = new Command('plug')
  .description(
    'Put a node on the grid (build + deploy + go live), or update if already there',
  )
  .argument('[target]', 'Optional path or URL for inspiration upload')
  .option('--existing <entity-id>', 'Bind this directory to an existing entity, then plug');
addGridOption(plugCommand, 'Pick the grid (first plug) or override the active grid for this call');
plugCommand
  .option('--no-deploy', 'Register the entity (init semantics) but do not build/deploy')
  .option(
    '--auto',
    'In an unlinked dir with no cloudgrid.yaml, detect the framework and set it up without a prompt (for CI / scripts)',
  )
  .option(
    '--no-auto-init',
    'In an unlinked dir, do not auto-create an entity — require an existing cloudgrid.yaml + link',
  )
  .option('--scope <scope>', 'Inspiration only — personal | space | org')
  .option('--space <slug>', 'Inspiration only — required when --scope space')
  .option('--visibility <visibility>', 'Inspiration only — private | space | org | link')
  .option('--name <slug>', 'Override the auto-derived slug (app auto-init or inspiration)')
  .option('--display-name <text>', 'Override the human-readable label')
  .option('--expires <days|never>', 'Inspiration only — default 30 days')
  .option('--pin-reason <text>', 'Inspiration only — required if --expires never')
  .option(
    '--inspired-by <entity-id>',
    'Repeatable. Records inspirations this node draws from.',
    collectRepeatable,
    [] as string[],
  )
  .option('--from-claude', 'Inspirations only — pick from Claude artifacts (deferred)')
  .option('--no-clipboard', "Don't copy the outlet URL to clipboard")
  .option('--no-notify', 'Skip the OS notification on terminal state')
  .option(
    '--force-missing-env',
    'Proceed even if .env.example keys are unset on the entity',
  )
  .option('--no-progress', 'Skip live Cloud Build progress lines under cloud_build')
  .option(
    '--timeout <minutes>',
    'How long to wait for the deploy before detaching (default 20). The build continues server-side past this; check progress with cloudgrid status.',
  )
  .option(
    '--detach',
    'Queue the deploy and exit; a background watcher posts the OS notification when it finishes',
  )
  .option(
    '--migrate-from-legacy <ns>',
    'One-time cutover from a pre-010 namespace (releases hostname claim, then deletes the legacy compute after rollout). See docs/operations/legacy-pre-cutover-migration.md.',
  )
  .option(
    '--skip-preflight',
    'Skip the client-side preflights (the tsc --noEmit type check and the legacy_pre_cutover warning). Use to deploy past a known type error or in CI that type-checks elsewhere.',
  )
  .option(
    '--from <version_id>',
    'Re-mint a new version from an older version\'s source (re-plug what worked). Requires a linked directory.',
  )
  .option('--json', 'Machine-readable output (single line, no chrome)')
  .option(
    '--verbose',
    'On deploy failure, also print the underlying technical detail (raw error text + build/runtime log tails). Default surface is the user-language message + suggested next steps.',
  )
  .action(async (target: string | undefined, opts: PlugOpts) => {
    const start = Date.now();
    try {
      await runPlug(target, opts);
      try {
        const { recordCliEvent } = await import('../services/telemetry.js');
        await recordCliEvent({
          event_type: 'success',
          command: 'cloudgrid plug',
          elapsed_ms: Date.now() - start,
        });
      } catch { /* swallow — success path must not regress on telemetry bugs */ }
    } catch (err) {
      // CloudGridError flows up to the index.ts top-level catch where it
      // gets a clean 1-liner. V2ApiError that escaped mapPlugServerError
      // (e.g. from unmapped paths) goes through handleV2Error so the
      // user sees a consistent code/exit-code mapping with other verbs.
      if (err instanceof V2ApiError) {
        await handleV2Error(err, { json: opts.json });
      }
      throw err;
    }
  });
